import React, { useState, useEffect, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import Dashboard from '@/pages/Dashboard';
import Discover from '@/pages/Discover';
import Profile from '@/pages/Profile';
import UserProfile from '@/pages/UserProfile';
import Messages from '@/pages/Messages';
import Subscription from '@/pages/Subscription';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import Settings from '@/pages/Settings';
import Contact from '@/pages/Contact';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Events from '@/pages/Events';
import Invite from '@/pages/Invite';
import CouchSurfing from '@/pages/CouchSurfing';
import GroupCalls from '@/pages/GroupCalls';
import CallHistory from '@/pages/CallHistory';
import ChatGroups from '@/pages/ChatGroups';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminAnalytics from '@/pages/AdminAnalytics';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import IncomingCallListener from '@/components/IncomingCallListener';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('keepSignedIn');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(to bottom right, #fff5f5, #f0fdfa)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: '#FF6B6B', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refreshUser: fetchUser }}>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
          <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <Auth />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/auth" />} />
          <Route path="/discover" element={user ? <Discover /> : <Navigate to="/auth" />} />
          <Route path="/events" element={user ? <Events /> : <Navigate to="/auth" />} />
          <Route path="/invite" element={user ? <Invite /> : <Navigate to="/auth" />} />
          <Route path="/couch-surfing" element={user ? <CouchSurfing /> : <Navigate to="/auth" />} />
          <Route path="/group-calls" element={user ? <GroupCalls /> : <Navigate to="/auth" />} />
          <Route path="/call-history" element={user ? <CallHistory /> : <Navigate to="/auth" />} />
          <Route path="/groups" element={user ? <ChatGroups /> : <Navigate to="/auth" />} />
          <Route path="/groups/:groupId" element={user ? <ChatGroups /> : <Navigate to="/auth" />} />
          <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/analytics" element={user?.role === 'admin' ? <AdminAnalytics /> : <Navigate to="/dashboard" />} />
          <Route path="/profile" element={user ? <Profile /> : <Navigate to="/auth" />} />
          <Route path="/user/:userId" element={user ? <UserProfile /> : <Navigate to="/auth" />} />
          <Route path="/messages" element={user ? <Messages /> : <Navigate to="/auth" />} />
          <Route path="/messages/:userId" element={user ? <Messages /> : <Navigate to="/auth" />} />
          <Route path="/subscription" element={user ? <Subscription /> : <Navigate to="/auth" />} />
          <Route path="/subscription-success" element={user ? <SubscriptionSuccess /> : <Navigate to="/auth" />} />
          <Route path="/settings" element={user ? <Settings /> : <Navigate to="/auth" />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <PWAInstallPrompt />
        {user && <IncomingCallListener />}
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthContext.Provider>
  );
}

export default App;